import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  buildUserErrorsHref,
  shouldAutoStickToBottom,
  validateTicketMessage,
} from '../lib/adminRequests';
import { useSuperAdminStore } from '../store/useSuperAdminStore';
import type { AdminTicketDto, AdminTicketPriority, AdminTicketStatus, AdminUserDto } from '../types';
import { AdminButton, useAdminToast } from './components';
import RequestsInbox from './RequestsInbox';
import { useRequestsRouteModel } from './useRequestsRouteModel';
import './styles/adminRequests.css';

const PriorityOptions: AdminTicketPriority[] = ['high', 'medium', 'low'];

const formatDate = (value: string) => new Intl.DateTimeFormat('en-GB', {
  dateStyle: 'medium',
  timeStyle: 'short',
}).format(new Date(value));


const MessageCard: React.FC<{ message: NonNullable<AdminTicketDto['messages']>[number] }> = ({ message }) => (
  <article className={`requests-message requests-message--${message.kind}`}>
    <header>
      <strong>{message.kind === 'note' ? 'Private internal note' : message.authorName}</strong>
      <time dateTime={message.createdAt}>{formatDate(message.createdAt)}</time>
    </header>
    {message.kind === 'note' && <p className="requests-message__privacy">Visible only to Super administrators.</p>}
    <p>{message.body}</p>
  </article>
);

const ThreadControls: React.FC<{
  assignees: AdminUserDto[];
  ticket: AdminTicketDto;
  onPatch: (patch: Partial<Pick<AdminTicketDto, 'status' | 'priority' | 'assigneeId'>>) => Promise<void>;
}> = ({ assignees, ticket, onPatch }) => (
  <div className="requests-thread__controls">
    <label>
      Status
      <select
        aria-label="Status"
        onChange={(event) => onPatch({ status: event.target.value as AdminTicketStatus })}
        value={ticket.status}
      >
        <option disabled value="new">New</option>
        <option value="open">Open</option>
        <option value="resolved">Resolved</option>
      </select>
    </label>
    <label>
      Priority
      <select
        aria-label="Priority"
        onChange={(event) => onPatch({ priority: event.target.value as AdminTicketPriority })}
        value={ticket.priority}
      >
        {PriorityOptions.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
      </select>
    </label>
    <label>
      Assignee
      <select
        aria-label="Assignee"
        onChange={(event) => onPatch({ assigneeId: event.target.value ? Number(event.target.value) : null })}
        value={ticket.assigneeId ?? ''}
      >
        <option value="">Unassigned</option>
        {assignees.map((assignee) => (
          <option key={assignee.id} value={assignee.id}>{assignee.username}</option>
        ))}
      </select>
    </label>
  </div>
);

const Composer: React.FC<{
  disabled: boolean;
  label: string;
  onSend: (body: string) => Promise<void>;
  placeholder: string;
}> = ({ disabled, label, onSend, placeholder }) => {
  const [body, setBody] = useState('');
  const [validation, setValidation] = useState<string | null>(null);
  const sendingRef = useRef(false);
  const submit = async () => {
    if (disabled || sendingRef.current) {
      return;
    }
    const error = validateTicketMessage(body);
    setValidation(error);
    if (error) {
      return;
    }
    try {
      sendingRef.current = true;
      await onSend(body.trim());
      setBody('');
    } catch {
      // The route-level alert shows the server/network error; keep the draft intact.
    } finally {
      sendingRef.current = false;
    }
  };

  return (
    <div className="requests-composer">
      <label>
        {label}
        <textarea
          aria-label={label}
          disabled={disabled}
          onChange={(event) => setBody(event.target.value)}
          onKeyDown={(event) => {
            if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
              event.preventDefault();
              void submit();
            }
          }}
          placeholder={placeholder}
          value={body}
        />
      </label>
      {validation && <p className="requests-error">{validation}</p>}
      <AdminButton disabled={disabled} onClick={() => void submit()} variant="primary">
        {label === 'Reply' ? 'Send reply' : 'Add internal note'}
      </AdminButton>
    </div>
  );
};

const EmptyThread: React.FC = () => (
  <section className="requests-thread requests-thread--empty" aria-label="Ticket thread">
    <h2>Select a request</h2>
    <p>Choose a ticket from the inbox to inspect the full conversation and metadata.</p>
  </section>
);

const useTicketThreadActions = (ticket: AdminTicketDto | null, assignees: AdminUserDto[]) => {
  const toast = useAdminToast();
  const patchTicket = useSuperAdminStore((state) => state.patchTicket);
  const sendTicketMessage = useSuperAdminStore((state) => state.sendTicketMessage);
  const submitting = useSuperAdminStore((state) => state.requestSubmitting);
  const updateMetadata = async (
    patch: Partial<Pick<AdminTicketDto, 'status' | 'priority' | 'assigneeId'>>
  ) => {
    if (!ticket) {
      return;
    }
    const optimistic = {
      status: patch.status ?? ticket.status,
      priority: patch.priority ?? ticket.priority,
      assigneeId: patch.assigneeId === undefined ? ticket.assigneeId : patch.assigneeId,
      assigneeName: patch.assigneeId === undefined
        ? ticket.assigneeName
        : assignees.find((assignee) => assignee.id === patch.assigneeId)?.username ?? null,
    };
    try {
      await patchTicket(ticket.id, { ...patch, expectedUpdatedAt: ticket.updatedAt }, optimistic);
    } catch {
      toast.showToast('Ticket changed. Refetched the latest thread before applying changes.', { tone: 'warning' });
    }
  };
  const sendMessage = async (kind: 'admin' | 'note', body: string) => {
    if (!ticket) {
      return;
    }
    await sendTicketMessage(ticket.id, { kind, body, expectedUpdatedAt: ticket.updatedAt });
    if (kind === 'admin') {
      toast.showToast(`Reply queued for ${ticket.userName ?? 'the requester'}`);
    }
  };
  return { sendMessage, submitting, updateMetadata };
};

const TicketThread: React.FC<{
  assignees: AdminUserDto[];
  ticket: AdminTicketDto | null;
  loading: boolean;
}> = ({ assignees, ticket, loading }) => {
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const shouldStickRef = useRef(true);
  const actions = useTicketThreadActions(ticket, assignees);

  useEffect(() => {
    shouldStickRef.current = true;
  }, [ticket?.id]);

  useLayoutEffect(() => {
    const element = messagesRef.current;
    if (!element || !ticket) {
      return;
    }
    if (shouldStickRef.current) {
      element.scrollTop = element.scrollHeight;
    }
  }, [ticket?.id, ticket?.messages?.length, ticket]);

  if (loading) {
    return <section className="requests-thread" role="status">Loading ticket thread…</section>;
  }
  if (!ticket) {
    return <EmptyThread />;
  }

  return (
    <section className="requests-thread" aria-label={`Ticket thread ${ticket.number}`}>
      <header className="requests-thread__header">
        <div>
          <p className="requests-thread__eyebrow">{ticket.number} · {ticket.category === 'ErrorReport' ? 'Error report' : ticket.category}</p>
          <h2>{ticket.subject}</h2>
          <p>{ticket.userName ?? 'Unknown user'} · {ticket.companyName || 'No company'} · Updated {formatDate(ticket.updatedAt)}</p>
        </div>
        {ticket.category === 'ErrorReport' && ticket.pcfRunTraceId && (
          <Link className="requests-thread__run-link" to={buildUserErrorsHref(ticket.pcfRunTraceId)}>
            Open run details
          </Link>
        )}
      </header>
      <ThreadControls assignees={assignees} ticket={ticket} onPatch={actions.updateMetadata} />
      <div className="requests-thread__actions">
        {ticket.status === 'resolved' ? (
          <AdminButton onClick={() => void actions.updateMetadata({ status: 'open' })}>Reopen</AdminButton>
        ) : (
          <AdminButton onClick={() => void actions.updateMetadata({ status: 'resolved' })}>Resolve</AdminButton>
        )}
      </div>
      <div
        className="requests-thread__messages"
        onScroll={(event) => {
          shouldStickRef.current = shouldAutoStickToBottom(event.currentTarget);
        }}
        ref={messagesRef}
      >
        {[...(ticket.messages ?? [])]
          .sort((left, right) => left.createdAt.localeCompare(right.createdAt))
          .map((message) => <MessageCard key={message.id} message={message} />)}
      </div>
      <div className="requests-thread__composers">
        <Composer disabled={actions.submitting} label="Reply" onSend={(body) => actions.sendMessage('admin', body)} placeholder="Reply to the requester. Ctrl+Enter sends." />
        <Composer disabled={actions.submitting} label="Internal note" onSend={(body) => actions.sendMessage('note', body)} placeholder="Private note visible only inside Super admin." />
      </div>
    </section>
  );
};

const RequestsRoute: React.FC = () => {
  const model = useRequestsRouteModel();

  return (
    <main className="admin-requests" data-ready={!model.requestLoading} data-testid="admin-requests">
      <RequestsInbox
        categoryCounts={model.requestCategoryCounts}
        draftSearch={model.draftSearch}
        error={model.requestError}
        listRef={model.listRef}
        loading={model.requestLoading}
        loadingMore={model.requestLoadingMore}
        nextCursor={model.requestNextCursor}
        onDraftSearch={model.setDraftSearch}
        onLoadMore={model.loadMore}
        onQueryPatch={model.updateQuery}
        pageSentinelRef={model.pageSentinelRef}
        query={model.query}
        statusCounts={model.requestStatusCounts}
        tickets={model.requestTickets}
      />
      <TicketThread
        assignees={model.assignees}
        loading={model.requestDetailLoading}
        ticket={model.requestDetail}
      />
    </main>
  );
};

export default RequestsRoute;
